export type ProductStatus = 'published' | 'draft' | 'archived' | 'scheduled'
export type StockLevel = 'in' | 'low' | 'out'
export type ProductVisibility = 'public' | 'members_only' | 'hidden'
export type ProductModalKind = 'delete' | 'bulkConfirm' | null
export type BulkProductAction = 'publish' | 'draft' | 'archive' | 'map' | 'delete'

// Bulk category/brand assignment ("Map") payload. Sent to the bulk products
// PUT endpoint alongside the {store_id, organization_id, product_ids} envelope.
// Field names mirror the per-product update payload (categories[], brand id).
export interface BulkAssignPayload {
  categories?: { id: number; name: string; slug: string }[]
  brand?: number | null
}
export type CannabinoidUnit = '%' | 'mg' | 'ml' | 'mg/ml' | 'mg/unit'
export type StoreType = 'cova' | 'greenline' | 'lightspeed' | 'flowhub'

export interface ProductImage {
  id: string
  src: string
  alt?: string
  title?: string
  caption?: string
  isFeatured?: boolean
  // Responsive set as returned by the backend: a width->URL map
  // (e.g. { '640': url, '1024': url }), or {} when no responsive set exists.
  srcset?: Record<string, string>
}

export interface CannabinoidEntry {
  name: 'THC' | 'CBD' | 'CBN' | 'CBG'
  min?: number
  max?: number
  unit: CannabinoidUnit
}

export type CannabinoidName = CannabinoidEntry['name']

// Custom specification card type (wire `bs_custom_specifications[].type`).
export type CustomSpecType = 'text' | 'longtext' | 'number' | 'range' | 'list'
export const CUSTOM_SPEC_TYPES: CustomSpecType[] = ['text', 'longtext', 'number', 'range', 'list']

// Wire shape of one `bs_custom_specifications[]` entry (snake_case like the
// other raw variation fields). Only `key` + `value` exist on legacy docs; a
// missing `type` reads as 'text'. For number/range/list the backend derives
// `value` (see utils/variationSpecs.ts formatCustomSpecValue); text/longtext
// store `value` as sent. Fields unused by the current type are kept so
// switching the type back restores them.
export interface CustomSpecification {
  key: string
  value: string
  type?: CustomSpecType
  number?: number | null
  min?: number | null
  max?: number | null
  unit?: string
  has_unit?: boolean
  items?: string[]
}

// Spec fields the merchant can hide in the editor (product-level, wire
// `bs_hidden_spec_fields`). Editor-only: hidden fields keep their values.
export type HiddenSpecField = CannabinoidName | 'terpenes' | 'effects' | 'flavours'
export const HIDDEN_SPEC_FIELDS: HiddenSpecField[] = ['THC', 'CBD', 'CBN', 'CBG', 'terpenes', 'effects', 'flavours']

// One variant's editable specification values. The product-level draft fields
// with the same names hold the default variant's (variation_group[0]) set.
export interface SpecSet {
  cannabinoids: CannabinoidEntry[]
  volume: number | null
  terpenes: string[]
  effects: string[]
  flavours: string[]
  bs_custom_specifications: CustomSpecification[]
}

// Spec state of a non-default variant. `inherit` mirrors the wire
// `bs_inherit_specs`; `specs` keeps the variant's own values even while it
// inherits, so turning inheritance off restores them.
export interface VariationSpecsEntry {
  inherit: boolean
  specs: SpecSet
}

export interface ProductLocationStock {
  locationId: string
  locationName: string
  stockQty: number
  threshold?: number
}

export interface ProductVariant {
  id: string
  name: string
  sku: string
  price: number
  salePrice?: number
  cogs?: number
  stock: StockLevel
  stockQty: number
  netWeight?: string
  cannabisEquiv?: string
  hcClass?: string
  gtin?: string
  isCurrent?: boolean
  availability?: string
}

export interface LinkedProduct {
  id: string
  name: string
  sku?: string
  price?: number
  stock_quantity?: number
  images?: { src: string }[]
}

export interface VariationGroupLocationInventory {
  available: number
  reserved: number
  booked: number
  in_transit: number
}

export interface VariationGroupLocationCovaMapping {
  CatalogItemId?: string
  LocationId?: string
  warehouse_id?: string
}

export interface VariationGroupLocation {
  id: number
  // Manual (unlink-POS) edit payload: the backend reads the variation name/SKU from
  // these per-location fields too.
  name?: string
  variant_sku?: string
  sku?: string
  price?: string
  regular_price?: string
  sale_price?: string
  bs_sale_price?: string | null
  bs_date_on_sale_from_gmt?: string | null
  bs_date_on_sale_to_gmt?: string | null
  date_on_sale_from_gmt?: string | null
  date_on_sale_to_gmt?: string | null
  location_name?: string
  stock_quantity?: number
  inventory_quantity?: number
  threshold?: number
  inventory?: VariationGroupLocationInventory
  inventory_id?: string | null
  // Top-level warehouse id sent on CREATE — each location entry in a new
  // product's variation_data must carry the warehouse it belongs to. On existing
  // (POS-synced) products this lives under cova_mapping instead.
  warehouse_id?: string
  cova_mapping?: VariationGroupLocationCovaMapping
  image?: { name?: string; alt?: string; src?: string }
  images?: { name?: string; alt?: string; src?: string }[]
  description?: string
  gtin?: string
  upc?: string
  _custom_product_meta_gtin?: string | null
  _custom_product_meta_upc?: string | null
  plant_type?: string
  weight?: number
  cannabis_weight?: number
  volume?: number | null
  volume_unit?: string
  thc_min?: number | null
  thc_max?: number | null
  thc_unit?: string
  cbd_min?: number | null
  cbd_max?: number | null
  cbd_unit?: string
  cbn_min?: number | null
  cbn_max?: number | null
  cbn_unit?: string
  cbg_min?: number | null
  cbg_max?: number | null
  cbg_unit?: string
  terpenes?: unknown[]
  terpenes_total?: number | null
  terpenes_total_unit?: string
  effects?: string[]
  flavours?: string[]
  bs_custom_specifications?: CustomSpecification[]
  // true = uses the default variant's specs; null/absent = never written.
  bs_inherit_specs?: boolean | null
  specifications?: Record<string, string | number | null>
  ids?: number[]
}

export interface VariationGroupCatalog {
  CatalogItemId?: string
  Manufacturer?: string
  ManufacturerSkus?: { Manufacturer?: string; SKU?: string }[]
  MasterName?: string
  MasterProductName?: string
  Name?: string
  Prices?: unknown[]
  SupplierSkus?: { Supplier?: string; SKU?: string }[]
  Upcs?: string[]
  VariationId?: string
}

// Wire shape of a variation's image sub-doc. The backend variation ImageSerializer
// currently whitelists name/alt/src only; `caption` round-trips once the backend
// ImageSerializer whitelists it (see be_libs/utils/pantry_product.py).
export interface VariationImage {
  name?: string
  alt?: string
  src?: string
  caption?: string
}

// Per-variation image edit stashed on the draft (keyed by VariationGroupItem._id).
// A `null` entry means the image was removed/cleared.
export type VariationImagePatch = Pick<VariationImage, 'src' | 'alt' | 'name' | 'caption'>

export interface VariationGroupItem {
  _id: string
  name?: string
  description?: string
  status?: string
  type?: string
  short_sku?: string
  // Sent on the manual (unlink-POS) edit/create payload — the backend reads the
  // variation SKU from `variant_sku` (on both item and locations).
  variant_sku?: string
  gtin?: string
  upc?: string
  sale_price?: string
  date_on_sale_from_gmt?: string | null
  date_on_sale_to_gmt?: string | null
  image?: VariationImage
  images?: VariationImage[]
  attributes?: { id: number; name: string; option: string }[]
  plant_type?: string
  weight?: number
  cannabis_weight?: number
  volume?: number | null
  volume_unit?: string
  thc_min?: number | null
  thc_max?: number | null
  thc_unit?: string
  cbd_min?: number | null
  cbd_max?: number | null
  cbd_unit?: string
  cbn_min?: number | null
  cbn_max?: number | null
  cbn_unit?: string
  cbg_min?: number | null
  cbg_max?: number | null
  cbg_unit?: string
  terpenes?: unknown[]
  terpenes_total?: number | null
  terpenes_total_unit?: string
  effects?: string[]
  flavours?: string[]
  bs_custom_specifications?: CustomSpecification[]
  // true = uses the default variant's specs; absent = never written (read as
  // inheriting for index > 0, see utils/variationSpecs.ts).
  bs_inherit_specs?: boolean | null
  ids?: number[]
  total_available?: number
  location_has_available?: number
  catalog_item_id?: string
  unaltered_slug?: string
  cost?: number
  deposit_fee?: number
  catalog?: VariationGroupCatalog
  locations?: VariationGroupLocation[]
}

export interface VariationLocationQuantity {
  name?: string
  variations: {
    name: string
    inventory_quantity: number
    stock_quantity: number
  }[]
}

export interface VariationQuantity {
  locations: Record<string, VariationLocationQuantity>
  total_quantity: number
  total_stock_quantity: number
}

export interface ProductListVariation {
  id: string
  name: string
  sku: string
  image?: ProductImage
  categoryName?: string
  subCategories?: string[]
  brand?: string
  price: number
  minPrice?: number
  maxPrice?: number
  salePrice?: number
  saleMinPrice?: number
  saleMaxPrice?: number
  stock: StockLevel
  stockQty: number
  status: ProductStatus
  date_created_gmt?: string
  quantity?: VariationQuantity
}

export interface Product {
  id: string
  name: string
  slug: string
  sku: string
  brand?: string
  categoryId?: string
  categoryIds?: string[]
  categoryName?: string
  subCategories?: string[]
  hcClass?: string
  price: number
  minPrice?: number
  maxPrice?: number
  salePrice?: number
  cogs?: number
  status: ProductStatus
  stock: StockLevel
  stockQty: number
  locationStock?: ProductLocationStock[]
  mapped: boolean
  image?: ProductImage
  images?: ProductImage[]
  shortDescription?: string
  longDescription?: string
  tags?: (string | number)[]
  terpenes?: string[]
  effects?: string[]
  flavours?: string[]
  cannabinoids?: CannabinoidEntry[]
  seoTitle?: string
  seoDescription?: string
  seoKeyphrase?: string
  canonicalUrl?: string
  netWeight?: string
  cannabisEquiv?: string
  posProductName?: string
  posSku?: string
  // Raw POS-link status: 'syncing' (POS-linked, incl. Budsense) | 'disconnect' (manual entry).
  // Defaults to 'syncing' at the mapper when the API omits the field, matching legacy behavior.
  posStatus?: string
  // True while a POS resync (triggered via productApis.syncFromPos) is still running on the backend.
  waitingSyncingFromPos?: boolean
  gtin?: string
  upc?: string
  variants?: ProductVariant[]
  variations?: ProductListVariation[]
  visibility?: ProductVisibility
  posIntegration?: string
  updatedAt?: string
  createdAt?: string
  date_created_gmt?: string
  publishedAt?: string
  scheduledAt?: string
  upsellIds?: string[]
  relatedIds?: string[]
  upsellData?: LinkedProduct[]
  relatedData?: LinkedProduct[]
  aiCredits?: { used: number; remaining: number; total: number }
  locationSpecificData?: boolean
  // Backend-computed Page Score (0-100) from the list payload's `page_score`.
  // Read-only; undefined when the API omits it or sends a non-finite value.
  pageScore?: number
  // Per-area breakdown from the list payload's `page_score_data.areas`, used by
  // the list's score tooltip. Undefined when the API omits or mangles it.
  pageScoreAreas?: ProductPageScoreAreaResult[]
  // Editor-hidden spec fields (`bs_hidden_spec_fields`); [] when never stored.
  hiddenSpecFields: HiddenSpecField[]
}

// One backend Page Score area (`page_score_data.areas[]`).
export interface ProductPageScoreAreaResult {
  id: PageScoreAreaId
  // Completion fraction in [0, 1].
  value: number
  // Backend status copy: 'Assigned' | 'Present' | 'Complete' | 'Missing' | 'Can be improved'.
  status: string
  // Failing sub-checks (Specifications / SEO only); empty otherwise.
  issues: string[]
}

// ---- Create-product wire types (POST /pantry/stores/{storeId}/products) ----
// These mirror the backend CreateProductSerializer exactly and are DISTINCT from
// the update path (VariationGroupItem[]): here `variation_data[]` are variants,
// and each variant's `locations[]` carries the full per-warehouse spec.
export interface CreateTerpene {
  name: string
  range: { gte: number | null; lte: number | null }
  unit: '%'
}

export interface CreateVariantImage {
  title?: string
  caption?: string
  name?: string
  alt?: string
  src?: string
  srcset?: Record<string, string>
}

export interface CreateVariantLocation {
  warehouse_id: string
  name: string
  regular_price: string
  variant_sku: string
  inventory_quantity: number
  // Cosmetic/forward-compat fields: the current backend ignores these (stock is
  // computed from inventory − warehouse threshold; deposit_fee is hardcoded;
  // threshold comes from warehouse config; cost is unused). Sent to match the
  // full request shape.
  stock_quantity?: number
  cost?: number
  deposit_fee?: number
  threshold?: number
  status?: string
  // Omitted → backend auto-generates `{variant_sku}-{last4 warehouse_id}`.
  sku?: string
  description?: string
  tax_class?: string
  gtin?: string
  upc?: string
  plant_type?: string
  terpenes: CreateTerpene[]
  terpenes_total?: number | null
  terpenes_total_unit: '%'
  effects?: string[]
  flavours?: string[]
  weight?: number | null
  cannabis_weight?: number | null
  units_per_package?: number | null
  unit_weight?: number | null
  volume?: number | null
  volume_unit?: string
  thc_min?: number | null
  thc_max?: number | null
  thc_unit: string
  cbd_min?: number | null
  cbd_max?: number | null
  cbd_unit: string
  cbn_min?: number | null
  cbn_max?: number | null
  cbn_unit: string
  cbg_min?: number | null
  cbg_max?: number | null
  cbg_unit: string
  images?: CreateVariantImage[]
}

export interface CreateVariant {
  name: string
  variant_sku: string
  locations: CreateVariantLocation[]
}

export interface CreateProductPayload {
  name: string
  slug: string
  description?: string
  short_description?: string
  bs_status?: string
  pos_status?: string
  external_datasource?: string
  date_created_gmt?: string
  images?: CreateVariantImage[]
  categories?: { id: number }[]
  // Single optional brand id; 0 = no brand (backend rejects null).
  brand?: number
  seo?: Record<string, string>
  featured?: boolean
  // Existing numeric tag ids only — the backend validates they exist.
  tags?: number[]
  cross_sell_ids?: number[]
  upsell_ids?: number[]
  related_ids?: number[]
  // Flags per-location pricing/stock. Currently ignored by the backend; sent to
  // match the full request shape.
  location_specific_data?: boolean
  variation_data: CreateVariant[]
}

export interface ProductDraft {
  name: string
  slug: string
  shortDescription: string
  longDescription: string
  images: ProductImage[]
  tags: (string | number)[]
  terpenes: string[]
  effects: string[]
  flavours: string[]
  bs_custom_specifications: CustomSpecification[]
  cannabinoids: CannabinoidEntry[]
  seoTitle: string
  seoDescription: string
  seoKeyphrase: string
  canonicalUrl: string
  volume: number | null
  // Product-level net weight / cannabis equivalent (grams). Applied to every
  // variation+location at save time — editable only in unlink-POS state.
  weight?: number | null
  cannabisWeight?: number | null
  gtin: string
  upc: string
  categoryIds: string[]
  categories?: { id: string; name: string; slug: string }[]
  brand: string
  status: ProductStatus
  scheduledAt: string
  visibility: ProductVisibility
  upsellIds: string[]
  relatedIds: string[]
  upsellData: LinkedProduct[]
  relatedData: LinkedProduct[]
  variationData?: VariationGroupItem[]
  // Per-variation image edits, keyed by VariationGroupItem._id. A `null` entry
  // clears the image. Applied onto each item in buildVariationData at save time.
  variationImageOverrides?: Record<string, VariationImagePatch | null>
  // Create-flow only: the assembled variation_data for the POST create endpoint,
  // produced by buildCreateVariationData. Never populated from the API — the edit
  // path uses variationData (VariationGroupItem[]) instead.
  createVariationData?: CreateVariant[]
  locationSpecificData?: boolean
  // Editor-hidden spec fields, sent as `bs_hidden_spec_fields`.
  hiddenSpecFields: HiddenSpecField[]
  // Spec state of the NON-default variations, keyed by VariationGroupItem._id.
  // The default variant (index 0) uses the product-level spec fields above.
  // Applied per item in buildVariationData at save time.
  variationSpecs: Record<string, VariationSpecsEntry>
}

export type ProductSortBy = 'name' | 'date_created_gmt' | 'page_score'

// Backend `page_score_states` filter values: good 75–100, weak 45–74, bad 0–44.
export type PageScoreState = 'good' | 'weak' | 'bad'
// Canonical order — used to normalise URL/filter state.
export const PAGE_SCORE_STATES: PageScoreState[] = ['good', 'weak', 'bad']

// Page Score areas — the editor engine's area ids and the backend
// `page_score_areas` filter values ("needs improvement": area value < 1).
export type PageScoreAreaId = 'brand' | 'category' | 'image' | 'description' | 'specs' | 'seo'
// Canonical order — used to normalise URL/filter state.
export const PAGE_SCORE_AREAS: PageScoreAreaId[] = ['brand', 'category', 'image', 'description', 'specs', 'seo']

export interface ProductListParams {
  page: number
  limit: number
  search?: string
  status?: ProductStatus | 'all'
  // Multi-select: the backend matches ANY of the ids (Mongo `$in`). Sent as
  // repeated `category_ids` / `brands` query keys.
  categoryIds?: string[]
  brandIds?: string[]
  stockQuantityMin?: number
  stockQuantityMax?: number
  locationId?: string
  hcClass?: string
  mapping?: 'mapped' | 'unmapped' | 'all'
  posStatus?: 'disconnect' | 'syncing'
  // Sent as repeated `page_score_states` keys. Omit/empty = all (incl. unscored).
  pageScoreStates?: PageScoreState[]
  // Sent as repeated `page_score_areas` keys (OR within, AND with states).
  pageScoreAreas?: PageScoreAreaId[]
  sortBy?: ProductSortBy
  sort?: 1 | -1
  // User UTC-offset (hours). Only sent on CSV export so the backend formats
  // datetime columns in the user's local time; ignored by the list query.
  timeZone?: number
}

// Export-CSV response. The backend returns one of two shapes:
//   • `{ csv }`       — small filtered sets stream the CSV body inline; the
//                       frontend downloads it immediately.
//   • `{ export_id }` — large sets are queued as an async backend job; the file
//                       is delivered later via the push-notification system.
export interface ProductExportResponse {
  csv?: string
  export_id?: string
}

export interface ProductListResponse {
  data: Product[]
  totalItems: number
  totalPages: number
  all: number
  published: number
  draft: number
  archived: number
  // Backend `page_score_needs_improvement`: count of products matching all
  // current filters (page-score ones included) that the "Needs Improvement"
  // card represents — score < 75 (Weak + Bad). NOTE: until the backend reverts
  // to the score-based count it counts products with any incomplete area.
  // Absent on non-pantry stores.
  pageScoreNeedsImprovement?: number
}
